import type { TerrainModel, GlobalSlope, DemCache, ContourLine, ElevationPoint } from './types';
import { roadBedAt } from './roadBeds';

/**
 * Returns the absolute ground elevation at the given lat/lng, in metres.
 *
 * Resolution priority (highest first):
 *   1. TIN interpolation from elevationPoints (WP-2 — not implemented yet)
 *   2. Contour-line interpolation (WP-7 — not implemented yet)
 *   3. demCache bilinear lookup (auto-fetched from Open-Meteo, WP-8)
 *   4. globalSlope (planar tilt) — manual MVP fallback
 *   5. flat: referenceElevationM
 *
 * Always returns 0 if terrain is undefined or `enabled === false`.
 */
export function elevationAt(
  terrain: TerrainModel | undefined,
  lat: number,
  lng: number,
): number {
  const base = elevationSampleAt(terrain, lat, lng).elevM;
  // A road's graded bed over the natural ground (roadBeds.ts): flat across a
  // carriageway, smooth along it, easing out over its side slopes.
  if (terrain?.enabled && terrain.roadBeds && Number.isFinite(base)) {
    const bed = roadBedAt(terrain.roadBeds, lat, lng);
    if (bed) return base + (bed.z - base) * bed.w;
  }
  return base;
}

/**
 * Where an elevation came from.
 *
 * `dem` / `tin` / `contour` are MEASURED. `slope` is a planar model the user
 * set deliberately. `reference` is neither: it is the flat fallback used when
 * the query lands outside every source, and it silently reports the camera's
 * own site elevation for ground that could be a valley 200 m below.
 *
 * That distinction was unavailable, so a sightline walking off the edge of a
 * 2 km DEM kept marching over an invented plateau and reported clear line of
 * sight with full confidence. At 500 m of range nothing ever left the grid and
 * it never mattered; at the ranges long-range thermal actually works over, it
 * is most of the ray.
 */
export type ElevationSource = 'none' | 'tin' | 'contour' | 'dem' | 'slope' | 'reference';

export interface ElevationSample {
  /** Absolute ground elevation in metres (0 when terrain is off). */
  elevM: number;
  source: ElevationSource;
}

/** `elevationAt` plus the provenance of the number. Same resolution order. */
export function elevationSampleAt(
  terrain: TerrainModel | undefined,
  lat: number,
  lng: number,
): ElevationSample {
  if (!terrain?.enabled) return { elevM: 0, source: 'none' };

  if (terrain.elevationPoints && terrain.elevationPoints.length >= 3) {
    return {
      elevM: terrain.referenceElevationM + interpolateTIN(terrain.elevationPoints, lat, lng),
      source: 'tin',
    };
  }

  if (terrain.contourLines && terrain.contourLines.length > 0) {
    return {
      elevM: terrain.referenceElevationM + interpolateContours(terrain.contourLines, lat, lng),
      source: 'contour',
    };
  }

  if (terrain.demCache) {
    const demDelta = bilinearDem(terrain.demCache, lat, lng);
    if (demDelta !== null) {
      return { elevM: terrain.referenceElevationM + demDelta, source: 'dem' };
    }
    // Out-of-bounds → fall through to globalSlope / flat below.
  }

  if (terrain.globalSlope) {
    return {
      elevM: terrain.referenceElevationM + slopeElevation(terrain.globalSlope, lat, lng),
      source: 'slope',
    };
  }

  return { elevM: terrain.referenceElevationM, source: 'reference' };
}

/** True when the elevation here is measured or explicitly modelled, rather than
 *  the flat fallback standing in for data we do not have. */
export function isElevationKnown(
  terrain: TerrainModel | undefined,
  lat: number,
  lng: number,
): boolean {
  const s = elevationSampleAt(terrain, lat, lng).source;
  return s === 'dem' || s === 'tin' || s === 'contour' || s === 'slope';
}

// ─── Global slope (WP-3 / MVP) ────────────────────────────────────────────

/**
 * Linear planar tilt. Returns delta in metres relative to the slope's
 * referenceLat/referenceLng. Uphill (opposite of azimuthDeg) is positive.
 */
function slopeElevation(slope: GlobalSlope, lat: number, lng: number): number {
  const { lat: refLat } = { lat: slope.referenceLat };
  // Equirectangular approximation — accurate within ~0.1% for distances under 1km.
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos((refLat * Math.PI) / 180);

  const dEastM = (lng - slope.referenceLng) * metersPerDegLng;
  const dNorthM = (lat - slope.referenceLat) * metersPerDegLat;
  const distM = Math.sqrt(dEastM * dEastM + dNorthM * dNorthM);
  if (distM < 1e-6) return 0;

  // Bearing from reference, clockwise from north.
  const bearingDeg = (Math.atan2(dEastM, dNorthM) * 180) / Math.PI;
  // Component along uphill axis (opposite of slope.azimuthDeg).
  // cos(bearing - azimuth) > 0 means moving downhill → elevation decreases.
  const alignment = Math.cos(((bearingDeg - slope.azimuthDeg) * Math.PI) / 180);
  const slopeFraction = slope.gradePct / 100;
  return -alignment * distM * slopeFraction;
}

// ─── Stubs for higher-precision sources (implemented in later WPs) ────────

function interpolateTIN(_points: ElevationPoint[], _lat: number, _lng: number): number {
  // WP-2 will implement Delaunay triangulation + barycentric interpolation.
  // For now, fall back to averaging — placeholder so the priority chain works.
  return _points.reduce((s, p) => s + p.elevationM, 0) / _points.length;
}

function interpolateContours(_lines: ContourLine[], _lat: number, _lng: number): number {
  // WP-7 will implement nearest-contour interpolation. Placeholder for now.
  return _lines.reduce((s, c) => s + c.elevationM, 0) / _lines.length;
}

function bilinearDem(dem: DemCache, lat: number, lng: number): number | null {
  // Returns null when the query is outside the cached grid, so callers can
  // fall back to a coarser source (globalSlope) instead of silently flattening.
  const u = (lng - dem.sw.lng) / (dem.ne.lng - dem.sw.lng);
  const v = (lat - dem.sw.lat) / (dem.ne.lat - dem.sw.lat);
  if (u < 0 || u > 1 || v < 0 || v > 1) return null;
  const col = u * (dem.cols - 1);
  const row = v * (dem.rows - 1);
  const c0 = Math.floor(col);
  const c1 = Math.min(c0 + 1, dem.cols - 1);
  const r0 = Math.floor(row);
  const r1 = Math.min(r0 + 1, dem.rows - 1);
  const fc = col - c0;
  const fr = row - r0;
  const e00 = dem.elevations[r0 * dem.cols + c0];
  const e01 = dem.elevations[r0 * dem.cols + c1];
  const e10 = dem.elevations[r1 * dem.cols + c0];
  const e11 = dem.elevations[r1 * dem.cols + c1];
  // Reject NoData / sentinel / corrupt samples. Values here are DELTAS from
  // referenceElevationM, so a real one is within a site's relief (a few km at
  // most); NaN, ±Infinity, ±1e38, or a raw fill value like -9999 / 32767
  // (minus the centre → still huge) all fail this. Fed into the terrain
  // heightmap such a value spikes the mesh into a black wall and crashes
  // Cesium geometry ("error 5"). Bail to null → caller falls back to
  // globalSlope / referenceElevationM (smooth, safe).
  if (!isFiniteDelta(e00) || !isFiniteDelta(e01) || !isFiniteDelta(e10) || !isFiniteDelta(e11)) {
    return null;
  }
  return (1 - fr) * ((1 - fc) * e00 + fc * e01) + fr * ((1 - fc) * e10 + fc * e11);
}

/** A plausible terrain delta (metres from the site's reference elevation):
 *  finite and within ±9000 m. Excludes NaN/Infinity and DEM NoData sentinels. */
function isFiniteDelta(e: number): boolean {
  return Number.isFinite(e) && e > -9000 && e < 9000;
}

// ─── Convenience helpers ─────────────────────────────────────────────────

/**
 * Elevation difference between two points: positive means the second point
 * is higher than the first. Used by FOV/DORI math to figure out whether the
 * target sits below or above the camera's mounting base.
 */
export function elevationDelta(
  terrain: TerrainModel | undefined,
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
): number {
  return elevationAt(terrain, toLat, toLng) - elevationAt(terrain, fromLat, fromLng);
}

/**
 * How far from (lat, lng) the elevation data actually reaches, in metres, or
 * Infinity when the source has no edge (a global slope, or flat-by-choice).
 *
 * Answers the question a long-range camera raises and nothing else could: is
 * this 8 km sightline crossing terrain we measured, or terrain we invented? A
 * DEM is fetched over a fixed radius (today 250 m to 2 km); past its edge every
 * sample silently becomes `referenceElevationM`, so the march sees a perfectly
 * flat plateau at the camera's own height and reports clear line of sight.
 *
 * Distance to the NEAREST edge of the grid, so it is the radius that holds in
 * every direction — the honest number to compare a camera's range against.
 */
export function measuredTerrainReachM(
  terrain: TerrainModel | undefined,
  lat: number,
  lng: number,
): number {
  if (!terrain?.enabled) return Infinity;
  // A slope model (or a deliberate flat site) is defined everywhere; it is an
  // assumption the user made explicitly, not one we made for them.
  if (terrain.globalSlope) return Infinity;
  if (terrain.elevationPoints?.length || terrain.contourLines?.length) return Infinity;
  const dem = terrain.demCache;
  if (!dem) return Infinity;

  const M_PER_DEG_LAT = 111_320;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
  const northM = (dem.ne.lat - lat) * M_PER_DEG_LAT;
  const southM = (lat - dem.sw.lat) * M_PER_DEG_LAT;
  const eastM = (dem.ne.lng - lng) * mPerDegLng;
  const westM = (lng - dem.sw.lng) * mPerDegLng;
  // Outside the grid entirely → nothing here is measured.
  return Math.max(0, Math.min(northM, southM, eastM, westM));
}

/**
 * Everything needed to source an elevation-derived claim at this point.
 *
 * Assembled from what the model already carries rather than invented: the DEM
 * knows its provider, its dataset label and when it was fetched, and its grid
 * spacing is the corner separation divided by the cell count. A coverage
 * polygon nobody can source is worth nothing in a tender no matter how it was
 * computed, and every field here is either read from the data or null.
 */
export interface TerrainProvenance {
  source: ElevationSource;
  /** True grid spacing in metres, or null when the source has no grid. */
  resolutionM: number | null;
  /** Provider + dataset + fetch date, or a plain description of the fallback. */
  datasetVersion: string;
  /** How far real data reaches from here. Infinity when nothing is assumed. */
  measuredReachM: number;
}

export function terrainProvenanceAt(
  terrain: TerrainModel | undefined,
  lat: number,
  lng: number,
): TerrainProvenance {
  const source = elevationSampleAt(terrain, lat, lng).source;
  const measuredReachM = measuredTerrainReachM(terrain, lat, lng);

  if (source === 'dem' && terrain?.demCache) {
    const dem = terrain.demCache;
    const M_PER_DEG_LAT = 111_320;
    const mPerDegLng = M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
    // Spacing between cell CENTRES, hence cols - 1 rather than cols.
    const wM = ((dem.ne.lng - dem.sw.lng) * mPerDegLng) / Math.max(1, dem.cols - 1);
    const hM = ((dem.ne.lat - dem.sw.lat) * M_PER_DEG_LAT) / Math.max(1, dem.rows - 1);
    // The coarser axis is what actually limits what the grid can resolve.
    const resolutionM = Math.max(wM, hM);
    const parts = [dem.source, dem.datasetLabel, dem.fetchedAt?.slice(0, 10)].filter(Boolean);
    return { source, resolutionM, datasetVersion: parts.join(' '), measuredReachM };
  }

  if (source === 'slope') {
    const g = terrain?.globalSlope;
    return {
      source,
      resolutionM: null,
      datasetVersion: g ? `manual slope ${g.gradePct}% at ${g.azimuthDeg}°` : 'manual slope',
      measuredReachM,
    };
  }

  if (source === 'none') {
    return { source, resolutionM: null, datasetVersion: 'terrain disabled', measuredReachM };
  }

  if (source === 'reference') {
    // The honest name for it. This is the flat fallback standing in for data we
    // do not have, and calling it anything else is how it came to be trusted.
    return {
      source,
      resolutionM: null,
      datasetVersion: `assumed flat at ${terrain?.referenceElevationM ?? 0} m`,
      measuredReachM,
    };
  }

  return { source, resolutionM: null, datasetVersion: source, measuredReachM };
}
